import { useState } from 'react';

import { ArrowTopRightOnSquareIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import { applyPathParams } from '@/api/fetcher';
import { LinkPreserveSearch } from '@/components/LinkPreserveSearch';
import { Page } from '@/components/layout/Page';
import { CanvasRoutes, RouterConfig } from '@/configs/routes';
import { StatusBadge } from '@/features/decisions/components/backtests/StatusBadge';
import { SCENARIOS_PAGE_SIZE, useScenarios } from '@/features/decisions/hooks/useScenarios';
import type { ScenarioSummary } from '@/features/decisions/scenarios.api.types';

const COLUMNS = ['Name', 'Status', 'Created', 'Execution Config', 'Execution Summary'] as const;

// "Jun 7, 2026". Fixed to UTC so a run submitted late in the day does not display as
// the previous date for anyone west of the timestamp's origin.
function formatCreated(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

// Non-navigating for now: the config viewer lands in a later commit, and the summary
// page still needs a run id to point at. Rendered rather than dropped because the
// columns are part of the agreed layout, and an empty cell reads as missing data.
function PlaceholderCell({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[14px] font-medium text-[#B0B2BD]">
      {label}
      <ArrowTopRightOnSquareIcon className="size-4 shrink-0" />
    </span>
  );
}

function StateRow({ children }: { children: React.ReactNode }) {
  return (
    <tr>
      <td colSpan={COLUMNS.length} className="px-6 py-10 text-center text-[14px] text-[#5C5F72]">
        {children}
      </td>
    </tr>
  );
}

function ScenarioRow({ scenario }: { scenario: ScenarioSummary }) {
  return (
    <tr className="border-b border-gray-100 last:border-0 hover:bg-[#F5F6F8]/70">
      <td className="px-6 py-4">
        <LinkPreserveSearch
          to={applyPathParams(RouterConfig[CanvasRoutes.DecisionsBacktestDetail].path, {
            scenarioRunId: scenario.scenario_run_id,
          })}
          className="text-[14px] font-medium text-[#274483] hover:text-[#4A6BAD]"
        >
          {scenario.label}
        </LinkPreserveSearch>
        {/* The id, not just the label: it is what ClickHouse partitions on, the MDS group_id,
            and the value a config must carry — so it is the thing people copy. */}
        <span className="mt-0.5 block text-[12px] text-[#9295A5]">
          <code>{scenario.scenario_run_id}</code>
          {' · '}
          {scenario.execution_count} {scenario.execution_count === 1 ? 'run' : 'runs'}
        </span>
      </td>
      <td className="px-6 py-4">
        <StatusBadge status={scenario.latest_status} />
      </td>
      <td className="px-6 py-4 text-[14px] text-[#5C5F72]">
        {formatCreated(scenario.latest_submitted_at ?? scenario.created_at)}
      </td>
      <td className="px-6 py-4">
        <PlaceholderCell label="View config" />
      </td>
      <td className="px-6 py-4">
        <PlaceholderCell label="View summary" />
      </td>
    </tr>
  );
}

export function BacktestList() {
  const [offset, setOffset] = useState(0);
  const { data, isPending, isError, error } = useScenarios({ offset });

  const scenarios = data?.scenarios ?? [];
  const total = data?.total ?? 0;
  const limit = data?.limit ?? SCENARIOS_PAGE_SIZE;
  const shownFrom = total === 0 ? 0 : offset + 1;
  const shownTo = offset + scenarios.length;
  const page = Math.floor(offset / limit) + 1;
  const hasPrev = offset > 0;
  const hasNext = shownTo < total;

  return (
    <Page>
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <h2 className="text-[16px] font-semibold text-[#1A1C2E]">Saved Results</h2>
          {!isPending && !isError && (
            <span className="text-[13px] text-[#9295A5]">
              {total} {total === 1 ? 'result' : 'results'}
            </span>
          )}
        </div>

        <table className="w-full border-collapse">
          <thead>
            <tr className="border-y border-gray-200 text-left text-[13px] font-semibold text-[#1A1C2E]">
              {COLUMNS.map((label) => (
                <th key={label} className="px-6 py-3 font-semibold">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isPending && <StateRow>Loading backtests…</StateRow>}
            {isError && (
              <StateRow>
                <span className="text-[#B4291F]">Could not load backtests. {error.message}</span>
              </StateRow>
            )}
            {!isPending && !isError && scenarios.length === 0 && (
              <StateRow>No backtests yet. Submit one to see it here.</StateRow>
            )}
            {!isPending &&
              !isError &&
              scenarios.map((scenario) => <ScenarioRow key={scenario.scenario_run_id} scenario={scenario} />)}
          </tbody>
        </table>

        <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4">
          <p className="text-[13px] text-[#9295A5]">
            {isPending || isError ? ' ' : `Showing ${shownFrom}–${shownTo} of ${total} results`}
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={!hasPrev}
              onClick={() => setOffset((o) => Math.max(0, o - limit))}
              aria-label="Previous page"
              className="inline-flex size-8 items-center justify-center rounded-md border border-gray-300 text-gray-600 enabled:hover:bg-gray-50 disabled:text-gray-400 disabled:opacity-40"
            >
              <ChevronLeftIcon className="size-4" />
            </button>
            <span
              aria-current="page"
              className="inline-flex size-8 items-center justify-center rounded-md bg-[#1E2F52] text-[13px] font-semibold text-white"
            >
              {page}
            </span>
            <button
              type="button"
              disabled={!hasNext}
              onClick={() => setOffset((o) => o + limit)}
              aria-label="Next page"
              className="inline-flex size-8 items-center justify-center rounded-md border border-gray-300 text-gray-600 enabled:hover:bg-gray-50 disabled:text-gray-400 disabled:opacity-40"
            >
              <ChevronRightIcon className="size-4" />
            </button>
          </div>
        </div>
      </div>
    </Page>
  );
}
